import React, { useEffect, useState } from "react";

import { Loader2, QrCode } from "lucide-react";
import { useTranslation } from "react-i18next";

import { createQrDataUrl } from "../../../common/HtmlDesigner/hooks/useQrDataUrl";

const CORNER_CLASSES = [
  "left-0 top-0 rounded-tl-2xl border-l-[3px] border-t-[3px]",
  "right-0 top-0 rounded-tr-2xl border-r-[3px] border-t-[3px]",
  "bottom-0 left-0 rounded-bl-2xl border-b-[3px] border-l-[3px]",
  "bottom-0 right-0 rounded-br-2xl border-b-[3px] border-r-[3px]",
];

const QR_SIZE_CLASS = "h-[220px] w-[220px] lg:h-[248px] lg:w-[248px]";

const QrCodePanel = ({ qrCode, offerUrl }) => {
  const { t } = useTranslation(["walletOffer"]);
  const [serverQrFailed, setServerQrFailed] = useState(false);
  const [generatedQr, setGeneratedQr] = useState(null);
  const [clientQrFailed, setClientQrFailed] = useState(false);
  const needsClientQr = (!qrCode || serverQrFailed) && Boolean(offerUrl);
  const src = qrCode && !serverQrFailed ? qrCode : generatedQr;
  const isUnavailable = !src && (!needsClientQr || clientQrFailed);

  useEffect(() => {
    if (!needsClientQr) return undefined;
    let cancelled = false;
    createQrDataUrl({ value: offerUrl })
      .then((url) => {
        if (!cancelled) setGeneratedQr(url);
      })
      .catch(() => {
        if (!cancelled) setClientQrFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [needsClientQr, offerUrl]);

  return (
    <div className="flex flex-col items-center text-center">
      <div className="relative p-[0.75rem]">
        {CORNER_CLASSES.map((cornerClass) => (
          <span
            key={cornerClass}
            aria-hidden="true"
            className={`absolute h-8 w-8 border-cfPrimary ${cornerClass}`}
          />
        ))}
        <div className="rounded-xl bg-white p-[0.75rem] shadow-sm ring-1 ring-gray-200">
          {src ? (
            <img
              src={src}
              alt={t("walletOffer:qr_alt")}
              className={`${QR_SIZE_CLASS} animate-fadeIn select-none`}
              draggable={false}
              onError={() => setServerQrFailed(true)}
            />
          ) : isUnavailable ? (
            <div
              className={`${QR_SIZE_CLASS} flex flex-col items-center justify-center gap-2 rounded-lg bg-gray-50 px-[1rem] text-sm text-gray-500`}
              role="alert"
            >
              <QrCode className="h-8 w-8 text-gray-400" aria-hidden="true" />
              {t("walletOffer:qr_unavailable")}
            </div>
          ) : (
            <div
              className={`${QR_SIZE_CLASS} flex flex-col items-center justify-center gap-2 rounded-lg bg-gray-50 text-sm text-gray-500`}
              role="status"
            >
              <Loader2
                className="h-6 w-6 animate-spin text-cfPrimary"
                aria-hidden="true"
              />
              {t("walletOffer:qr_generating")}
            </div>
          )}
        </div>
      </div>

      <p className="mt-[1rem] text-sm font-semibold text-cfHeadingText">
        {t("walletOffer:qr_caption")}
      </p>
      <p className="mt-1 max-w-[18rem] text-sm leading-relaxed text-gray-500">
        {t("walletOffer:qr_helper")}
      </p>
    </div>
  );
};

export default QrCodePanel;
